import { Component } from '@theme/component';
import { debounce, startViewTransition } from '@theme/utilities';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Search query parameter (same as predictive search and facets).
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * A custom element that allows users to search collection products using the full store search
 * (same as predictive search). Uses the Section Rendering API so the main-collection section
 * is rendered with search context—same product cards as the collection (from collection.json).
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} resetButton - The reset button element.
 * @extends {Component<Refs>}
 */
class CollectionSearchInputComponent extends Component {
  requiredRefs = ['searchInput', 'resetButton'];

  #controller = new AbortController();
  #commitSearchOnIdle = () => {};

  /**
   * Get the section ID for Section Rendering (main-collection section).
   * @returns {string} The section ID
   */
  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    this.#commitSearchOnIdle = debounce(() => {
      this.#performSearch({ useTransition: false });
    }, this.#getDebounceMs());

    // If URL or input has a search term on load, re-render section with search URL (collection card styling)
    const initialValue = this.#getSearchTerm();
    if (initialValue.length > 0) {
      this.#showResetButton();
      this.#performSearch({ useTransition: false });
    }

    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * When facets (or other filters) update, section re-renders; if we're in "search mode" (q in URL),
   * re-render with search URL so the grid stays in sync.
   * @param {Event} event - The filter update event.
   */
  #handleFilterUpdate = (event) => {
    // Ignore our own sync event and avoid interrupting active typing.
    if (event?.target === this) return;
    if (document.activeElement === this.refs.searchInput) return;

    const term = this.#getSearchTerm();
    if (term.length === 0) return;
    setTimeout(() => this.#renderSectionWithSearchURL(term, false), 400);
  };

  /**
   * Handles search input with debouncing.
   */
  handleSearchInput = () => {
    const hasTerm = this.#getSearchTerm().length > 0;
    if (hasTerm) {
      this.#showResetButton();
    } else {
      this.#hideResetButton();
    }
    this.#commitSearchOnIdle();
  };

  /**
   * Handles keyboard events (Escape clear, Enter submit).
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (this.refs.searchInput.value === '') {
        this.refs.searchInput.blur();
      } else {
        this.handleClearClick();
      }
    } else if (event.key === 'Enter') {
      // Idle pause is the single commit trigger.
      event.preventDefault();
    }
  };

  /**
   * Handles clear button click: restore collection grid and URL.
   */
  handleClearClick = () => {
    this.refs.searchInput.value = '';
    this.refs.searchInput.focus();
    this.#hideResetButton();
    this.#performSearch({ useTransition: false });
  };
  /**
   * @returns {number} Debounce delay in milliseconds.
   */
  #getDebounceMs() {
    const rawValue = Number.parseInt(this.getAttribute('search-debounce-ms') || '', 10);
    if (Number.isNaN(rawValue)) return 250;
    return Math.max(rawValue, 0);
  }

  /**
   * @returns {boolean} Whether current URL already reflects current search term.
   */
  #isURLSyncedWithSearchTerm() {
    const url = new URL(window.location.href);
    const currentURLTerm = (url.searchParams.get(SEARCH_QUERY) || '').trim();
    return currentURLTerm === this.#getSearchTerm();
  }


  /**
   * @returns {string} Current search term from input, trimmed (raw for URL).
   */
  #getSearchTerm() {
    return this.refs.searchInput.value.trim();
  }

  /**
   * Builds the store search URL (same as predictive search: q, type=product, options[prefix]=last).
   * Used when requesting the main-collection section so it renders with search context (collection card styling).
   * @param {string} term - Search term
   * @returns {URL}
   */
  #getSearchContextURL(term) {
    const base = new URL(
      typeof Theme !== 'undefined' && Theme?.routes?.search_url
        ? Theme.routes.search_url
        : '/search',
      location.origin
    );
    base.searchParams.set(SEARCH_QUERY, term);
    base.searchParams.set('type', 'product');
    base.searchParams.set('options[prefix]', 'last');
    return base;
  }

  /**
   * Builds the collection page URL (current path, optional q, no page).
   * @param {string} [searchTerm] - If provided, add q param
   * @returns {string}
   */
  #getCollectionPageURL(searchTerm) {
    const url = new URL(window.location.pathname, location.origin);
    if (searchTerm) {
      url.searchParams.set(SEARCH_QUERY, searchTerm);
    }
    url.searchParams.delete('page');
    return url.toString();
  }

  /**
   * Performs the search: update URL and re-render the main-collection section.
   * When there is a search term we pass the search URL so the section renders with search.results
   * and the same product card blocks as the collection (collection.json).
   */
  async #performSearch({ useTransition = true } = {}) {
    const searchTerm = this.#getSearchTerm();

    // Avoid extra morph/event churn when idle timer fires with no effective changes.
    if (this.#isURLSyncedWithSearchTerm()) return;

    this.#updateURL(searchTerm);

    if (searchTerm.length > 0) {
      this.#showResetButton();
      await this.#renderSectionWithSearchURL(searchTerm, useTransition);
    } else {
      this.#hideResetButton();
      await this.#renderSectionWithCurrentURL(useTransition);
    }

    this.#dispatchFilterUpdate();
  }

  /**
   * Updates the URL with the search query (same param as predictive search).
   * @param {string} searchTerm - The search term (empty = clear q)
   */
  #updateURL(searchTerm) {
    const url = this.#getCollectionPageURL(searchTerm || undefined);
    history.replaceState({}, '', url);
  }

  /**
   * Dispatch FilterUpdate so facets and results-list stay in sync with URL.
   */
  #dispatchFilterUpdate() {
    const url = new URL(window.location.href);
    const queryParams = new URLSearchParams(url.search);
    this.dispatchEvent(new FilterUpdateEvent(queryParams));
  }

  /**
   * Re-renders the main-collection section with the search URL so Shopify renders
   * the section in search context (search.results) with the collection's product card blocks.
   * @param {string} term - Search term
   */
  async #renderSectionWithSearchURL(term, useTransition = true) {
    const searchContextURL = this.#getSearchContextURL(term);
    const viewTransition = useTransition && !this.closest('dialog');

    if (viewTransition) {
      await startViewTransition(
        () => sectionRenderer.renderSection(this.sectionId, { url: searchContextURL }),
        ['product-grid']
      );
    } else {
      await sectionRenderer.renderSection(this.sectionId, { url: searchContextURL });
    }
  }

  /**
   * Re-renders the main-collection section with the current URL (collection, no q).
   */
  async #renderSectionWithCurrentURL(useTransition = true) {
    const viewTransition = useTransition && !this.closest('dialog');

    if (viewTransition) {
      await startViewTransition(
        () => sectionRenderer.renderSection(this.sectionId),
        ['product-grid']
      );
    } else {
      await sectionRenderer.renderSection(this.sectionId);
    }
  }

  #showResetButton() {
    const { resetButton } = this.refs;
    resetButton.style.opacity = '1';
    resetButton.style.visibility = 'visible';
    resetButton.style.pointerEvents = 'auto';
  }

  #hideResetButton() {
    const { resetButton } = this.refs;
    resetButton.style.opacity = '0';
    resetButton.style.visibility = 'hidden';
    resetButton.style.pointerEvents = 'none';
  }
}

if (!customElements.get('collection-search-input')) {
  customElements.define('collection-search-input', CollectionSearchInputComponent);
}
